/*
 * Copyright (c) 2016-present, Parse, LLC
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import SashiDoLogo          from 'components/SashiDoLogo/SashiDoLogo.react';
import Button               from 'components/Button/Button.react';
import Field                from 'components/Field/Field.react';
import Icon                 from 'components/Icon/Icon.react';
import Popover              from 'components/Popover/Popover.react';
import Label                from 'components/Label/Label.react';
import React                from 'react';
import PropTypes            from 'prop-types';
import styles               from 'components/Modal/Modal.scss';

let buttonColors = {
  danger: 'red',
  info: 'blue',
  valid: 'green'
};

let Modal = (({
  type = Modal.Types.INFO,
  icon,
  iconSize = 36,
  children,
  title,
  subtitle,
  theme,
  footerDetails = null,
  cancelText = 'Cancel',
  onCancel,
  canCancel = true,
  showCancel = true,
  showCancelIcon,
  confirmText = 'Okay',
  onConfirm,
  disabled = false,
  progress = false,
  customFooter,
  hideFooter,
  textModal = false,
  width,
  bottom,
  buttonsInCenter = React.Children.count(children) === 0,
}) => {
  if (children) {
    children = React.Children.map(children, (c) => {
      if (c && c.type === Field && c.props.label) {
        return React.cloneElement(c, { ...c.props, labelPadding: 24 });
      }
      return c;
    });
  }

  let footer =
    !hideFooter ?
      customFooter
        ?
          <div style={{textAlign: buttonsInCenter ? 'center' : 'right'}} className={styles.footer}>{customFooter}</div>
        :
          <div className={styles.footer}>
            <div className={styles.details}>
              {footerDetails}
            </div>
            {
              (showCancel && cancelText && onCancel) &&
                <Button
                  value={cancelText}
                  onClick={onCancel}
                  disabled={!canCancel} />
            }

            {
              confirmText && onConfirm &&
                <Button
                  primary={true}
                  value={confirmText}
                  color={buttonColors[type]}
                  disabled={!!disabled}
                  onClick={onConfirm}
                  progress={progress} />
            }
          </div>
    : null;

  let CancelIcon =
    showCancelIcon
      ?
        <a href='javascript:;' className={styles.iconClose} onClick={onCancel}>
          <Icon width={16} height={16} name={'icon-close'} />
        </a>
      : null

  let wrappedChildren = textModal ? <div className={styles.textModal}>
    {children}
  </div> : children;

  let subtitle_element = subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null;

  return (
    <Popover modal={true}>
      <div className={styles.modalHolder}>
        <div className={[styles.modal, styles[type]].join(' ')} style={{ width: width, bottom: bottom }}>
          { title
            ? <div className={styles.header}>
                <div className={styles.title}>
                  {title}
                  {subtitle_element}
                </div>
              </div>
            : null
          }
          {wrappedChildren}
          { footer ? footer : null }
        </div>
        <SashiDoLogo link={true} fade={true} />
        {CancelIcon}
      </div>
    </Popover>
  );
});

Modal.Types = {
  DANGER: 'danger',
  INFO: 'info',
  VALID: 'valid',
  APPCREATE: 'appCreate'
};

Modal.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  cancelText: PropTypes.string,
  onCancel: PropTypes.func,
  canCancel: PropTypes.bool,
  showCancel: PropTypes.bool,
  confirmText: PropTypes.string,
  onConfirm: PropTypes.func,
  disabled: PropTypes.bool,
  progress: PropTypes.bool,
  customFooter: PropTypes.node,
  textModal: PropTypes.bool,
  width: PropTypes.number,
  bottom: PropTypes.number,
  buttonsInCenter: PropTypes.bool,
};

export default Modal;
